/**
 * StripoDialog component
 *
 * Full-screen dialog wrapper for the Stripo editor with:
 * - Header with title and action buttons
 * - Unsaved changes confirmation dialog
 * - ESC key and outside click handling
 */
import React, { useState, useCallback, useEffect } from 'react';
import { StripoWrapper } from './StripoWrapper';
import type { StripoMergeField } from './stripo.types';
import styles from './Stripo.module.css';

export interface StripoDialogProps {
  onClose: () => void;
  html: string;
  css: string;
  onSave: (html: string, css: string, compiledHtml: string) => void;
  mergeFields: StripoMergeField[];
}

export const StripoDialog: React.FC<StripoDialogProps> = ({
  onClose,
  html,
  css,
  onSave,
  mergeFields,
}) => {
  const [saveClicked, setSaveClicked] = useState(false);
  const [cancelClicked, setCancelClicked] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [checkForUnsavedChanges, setCheckForUnsavedChanges] = useState<(() => boolean) | null>(null);

  // Handle browser back button
  useEffect(() => {
    // Push a state when the modal opens so back button closes it instead of navigating away
    window.history.pushState({ stripoEditorOpen: true }, '');

    const handlePopState = () => {
      // Back button was pressed - close the modal
      const hasUnsaved = checkForUnsavedChanges?.() ?? false;
      if (hasUnsaved) {
        // Re-push state to prevent navigation, show confirmation
        window.history.pushState({ stripoEditorOpen: true }, '');
        setShowUnsavedChangesDialog(true);
      } else {
        onClose();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [checkForUnsavedChanges, onClose]);

  // Handle discard changes
  const handleDiscardChanges = useCallback(() => {
    setShowUnsavedChangesDialog(false);
    setCancelClicked(false);
    onClose();
  }, [onClose]);

  // Handle cancel discard
  const handleCancelDiscard = useCallback(() => {
    setShowUnsavedChangesDialog(false);
    setCancelClicked(false);
  }, []);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();

        const hasUnsaved = checkForUnsavedChanges?.() ?? false;
        if (hasUnsaved) {
          setShowUnsavedChangesDialog(true);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [checkForUnsavedChanges, onClose]);

  // Handle save completion
  const handleSave = useCallback(
    (newHtml: string, newCss: string, compiledHtml: string) => {
      onSave(newHtml, newCss, compiledHtml);
    },
    [onSave],
  );

  return (
    <>
      {/* Main Dialog */}
      <div className={styles.overlay}>
        <div className={styles.dialog}>
          {/* Header */}
          <header className={styles.header}>
            <h2 className={styles.title}>
              Email Editor
              <button
                className="toggle-mobile-button"
                title="Mobile View"
                aria-label="Switch to mobile view">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
              </button>
              <button
                className="toggle-desktop-button"
                title="Desktop View"
                aria-label="Switch to desktop view">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </button>
              <button
                className="toggle-code-editor-button"
                title="Code Editor"
                aria-label="Open code editor">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <polyline points="16 18 22 12 16 6" />
                  <polyline points="8 6 2 12 8 18" />
                </svg>
              </button>
            </h2>
            <nav className={styles.actions}>
              <button onClick={() => setCancelClicked(true)}>Cancel</button>
              <button onClick={() => setSaveClicked(true)} disabled={!isEditorReady}>
                Save
              </button>
            </nav>
          </header>

          {/* Editor */}
          <main className={styles.main}>
            <StripoWrapper
              html={html}
              css={css}
              onClose={onClose}
              saveClicked={saveClicked}
              setSaveClicked={setSaveClicked}
              cancelClicked={cancelClicked}
              setCancelClicked={setCancelClicked}
              onSave={handleSave}
              onEditorReadyChange={setIsEditorReady}
              setShowUnsavedChangesDialog={setShowUnsavedChangesDialog}
              setCheckForUnsavedChanges={setCheckForUnsavedChanges}
              mergeFields={mergeFields}
            />
          </main>
        </div>
      </div>

      {/* Unsaved Changes Confirmation Dialog */}
      {showUnsavedChangesDialog && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmDialog}>
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Are you sure you want to discard them and close the editor?</p>
            <nav>
              <button onClick={handleCancelDiscard}>Cancel</button>
              <button onClick={handleDiscardChanges}>Discard Changes</button>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};
